import React, { useEffect, useRef, useState } from "react";
import styles from "./Form.module.css";

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [textColor, setTextColor] = useState("#000000");
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorInputRef = useRef(null);
  const colorPickerRef = useRef(null);
  const colorButtonRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  useEffect(() => {
    if (document.queryCommandSupported && document.queryCommandSupported("styleWithCSS")) {
      document.execCommand("styleWithCSS", false, true);
    }
    if (document.queryCommandSupported && document.queryCommandSupported("enableObjectResizing")) {
      document.execCommand("enableObjectResizing", false, true);
    }
    if (document.queryCommandSupported && document.queryCommandSupported("enableInlineTableEditing")) {
      document.execCommand("enableInlineTableEditing", false, true);
    }
  }, []);

  const execCommand = (command, arg = null) => {
    if (document.queryCommandSupported && document.queryCommandSupported(command)) {
      document.execCommand(command, false, arg);
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML);
      }
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    const trimmed = text.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      execCommand("insertHTML", `<a href="${trimmed}" target="_blank" rel="noopener noreferrer">${trimmed}</a>`);
      return;
    }
    document.execCommand("insertText", false, text);
  };

  const handleCreateLink = () => {
    if (!editorRef.current) return;
    let url = window.prompt("Enter URL", "https://");
    if (!url) return;
    url = url.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    if (!selectedText) {
      execCommand("insertHTML", `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`);
    } else {
      execCommand("createLink", url);
    }
  };

  const handleUnlink = () => {
    execCommand("unlink");
  };

  const handleFormatCode = () => {
    execCommand("formatBlock", "PRE");
  };

  const fileInputRef = React.useRef(null);

  const handleInsertImage = () => {
    fileInputRef.current?.click();
  };

  const handleUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (src && editorRef.current) {
        editorRef.current.focus();
        const html = `<img src="${src}" style="max-width:100%;height:auto;display:block;resize:both;overflow:auto;margin:8px 0;" />`;
        if (document.queryCommandSupported && document.queryCommandSupported("insertHTML")) {
          execCommand("insertHTML", html);
        } else {
          execCommand("insertImage", src);
        }
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSelect = (e, command) => {
    const value = e.target.value;
    if (value) {
      execCommand(command, value);
    }
    e.target.value = "";
  };

  useEffect(() => {
    if (!showColorPicker) return;
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current?.contains(event.target) ||
        colorButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setShowColorPicker(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColorPicker]);

  const handleColorToggle = () => {
    setShowColorPicker((prev) => !prev);
  };

  const handleColorClick = () => {
    colorInputRef.current?.click();
  };

  const presetColors = [
    "#000000",
    "#dc2626",
    "#ea580c",
    "#f59e0b",
    "#16a34a",
    "#0ea5e9",
    "#7c3aed",
    "#db2777",
    "#2563eb",
    "#475569",
  ];

  const getRandomColor = () => {
    const colors = [
      "#ef4444",
      "#f97316",
      "#fde047",
      "#22c55e",
      "#38bdf8",
      "#818cf8",
      "#ec4899",
      "#0f766e",
      "#6d28d9",
      "#334155",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const applyColor = (color) => {
    setTextColor(color);
    execCommand("foreColor", color);
  };

  const handleColorChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    applyColor(value);
  };

  const toolbarButton = (label, command, arg = null, onClick) => (
    <button
      type="button"
      className={styles.toolbarButton}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick ?? (() => execCommand(command, arg))}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.editorWrapper}>
      <div className={styles.toolbar}>
        {toolbarButton("B", "bold")}
        {toolbarButton("I", "italic")}
        {toolbarButton("U", "underline")}
        {toolbarButton("S", "strikeThrough")}
        <select className={styles.toolbarSelect} onChange={(e) => handleSelect(e, "formatBlock")} defaultValue="">
          <option value="">Block</option>
          <option value="P">Paragraph</option>
          <option value="H1">Heading 1</option>
          <option value="H2">Heading 2</option>
          <option value="H3">Heading 3</option>
          <option value="BLOCKQUOTE">Quote</option>
        </select>
        <select className={styles.toolbarSelect} onChange={(e) => handleSelect(e, "fontSize")} defaultValue="">
          <option value="">Size</option>
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Huge</option>
        </select>
        <div className={styles.colorPickerWrapper} ref={colorPickerRef}>
          <button
            ref={colorButtonRef}
            type="button"
            className={`${styles.toolbarButton} ${styles.colorButton}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleColorToggle}
          >
            <span className={styles.colorButtonLabel}>A</span>
            <span className={styles.colorMark} style={{ backgroundColor: textColor }} />
          </button>
          {showColorPicker && (
            <div className={styles.colorPopup}>
              <div className={styles.colorSwatchGrid}>
                {presetColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={styles.colorSwatch}
                    style={{ backgroundColor: color, borderColor: color === textColor ? "#111" : "transparent" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyColor(color)}
                    title={color}
                  />
                ))}
              </div>
              <div className={styles.colorPopupActions}>
                <div className={styles.colorCustomButtonWrapper}>
                  <button
                    type="button"
                    className={`${styles.toolbarButton} ${styles.colorPopupButton}`}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    Custom
                  </button>
                  <input
                    ref={colorInputRef}
                    type="color"
                    value={textColor}
                    className={styles.hiddenColorInput}
                    onChange={handleColorChange}
                  />
                </div>
                <button
                  type="button"
                  className={`${styles.toolbarButton} ${styles.colorPopupButton}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyColor(getRandomColor())}
                >
                  Random
                </button>
              </div>
            </div>
          )}
        </div>
        {toolbarButton("OL", "insertOrderedList")}
        {toolbarButton("UL", "insertUnorderedList")}
        {toolbarButton("Link", null, null, handleCreateLink)}
        {toolbarButton("Unlink", null, null, handleUnlink)}
        {toolbarButton("Code", null, null, handleFormatCode)}
        {toolbarButton("Upload Img", null, null, handleInsertImage)}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleUploadImage}
        />
        {toolbarButton("Clear", "removeFormat")}
      </div>
      <div
        ref={editorRef}
        className={styles.editor}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder}
      />
    </div>
  );
}
